// v2.1 (D): the auth layer's own little store: /api/config, Clerk's status, the Clerk sign-out hook and the last
// session error. `authReady()` resolves once the config is known and (with Clerk) clerk-js has loaded or given up,
// so boot() never asks /api/me before the Clerk token exists.
import { useSyncExternalStore } from "react";
import { PASSWORD_ONLY, parseConfig, type AuthConfig, type ClerkStatus } from "./gate";
import { initMonitoring, parseSentryConfig } from "../monitoring";
import { configureAnalytics, parsePosthogConfig } from "../analytics";

export type AuthState = {
  config: AuthConfig | null;
  clerk: ClerkStatus | null;
  /** why the last session check failed while Clerk said signed in (shown on the session-error screen) */
  sessionError: string | null;
};

let state: AuthState = { config: null, clerk: null, sessionError: null };
const subs = new Set<() => void>();
export const getAuth = () => state;
export function setAuth(p: Partial<AuthState>) {
  state = { ...state, ...p };
  subs.forEach((f) => f());
  settle();
}
export function useAuthState<T>(pick: (s: AuthState) => T): T {
  return useSyncExternalStore(
    (f) => {
      subs.add(f);
      return () => subs.delete(f);
    },
    () => pick(state),
  );
}

// ------------------------------------------------------------------ readiness

let resolveReady: () => void = () => {};
const ready = new Promise<void>((r) => (resolveReady = r));
function settle() {
  const { config, clerk } = state;
  if (!config) return;
  if (!config.clerkPublishableKey || config.authMode === "password" || clerk?.loaded || clerk?.failed) resolveReady();
}
export const authReady = () => ready;

// ------------------------------------------------------------------ config

export const MOCK = import.meta.env.VITE_MOCK === "1";
let loading: Promise<AuthConfig> | null = null;

/** GET /api/config once. Mock mode and an api that predates it both mean password only. */
export function loadAuthConfig(): Promise<AuthConfig> {
  loading ??= (async () => {
    let cfg = PASSWORD_ONLY;
    // mock only: ?clerk=both|clerk mounts Clerk with an unreachable key, to see the "Clerk didn't load" fallbacks
    const mockClerk = MOCK ? new URLSearchParams(location.search).get("clerk") : null;
    if (mockClerk === "both" || mockClerk === "clerk") cfg = { clerkPublishableKey: `pk_test_${btoa("mock.clerk.invalid$")}`, authMode: mockClerk };
    // mock only (v4 LG2): ?signup=closed shows the "not in your country yet" sign-up screen
    if (MOCK && new URLSearchParams(location.search).get("signup") === "closed") cfg = { ...cfg, signupBlocked: true };
    if (!MOCK) {
      try {
        const r = await fetch("/api/config", { credentials: "include" });
        if (r.ok) {
          const raw: unknown = await r.json();
          cfg = parseConfig(raw);
          void initMonitoring(parseSentryConfig(raw)); // error reporting, when the api has SENTRY_WEB_DSN
          configureAnalytics(parsePosthogConfig(raw)); // PostHog, when the api has POSTHOG_KEY + POSTHOG_WEB=1
        }
      } catch {
        /* api unreachable: boot() reports it; the password form is the safe default */
      }
    }
    setAuth({ config: cfg, clerk: cfg.clerkPublishableKey && cfg.authMode !== "password" ? { loaded: false, signedIn: false, failed: false } : null });
    return cfg;
  })();
  return loading;
}

// ------------------------------------------------------------------ Clerk hooks the rest of the app can call

let clerkSignOut: (() => Promise<void>) | null = null;
export function setClerkSignOut(f: (() => Promise<void>) | null) {
  clerkSignOut = f;
}
/** Signs out of Clerk too (no-op without Clerk). */
export async function signOutClerk() {
  await clerkSignOut?.().catch(() => undefined);
}

// ------------------------------------------------------------------ an invite code carried through Clerk sign-up

const INVITE_KEY = "cao.inviteCode";
export function rememberInvite(code: string) {
  try {
    sessionStorage.setItem(INVITE_KEY, code);
  } catch {
    /* private mode: the user types it instead */
  }
}
export function rememberedInvite(): string | null {
  try {
    return sessionStorage.getItem(INVITE_KEY);
  } catch {
    return null;
  }
}
export function forgetInvite() {
  try {
    sessionStorage.removeItem(INVITE_KEY);
  } catch {
    /* nothing kept */
  }
}
